/**
 * PDF 페이지의 PNG 를 표시하는 핵심 컴포넌트.
 *
 * 구조 (M11/B7-a fix):
 *   ┌─ relative wrapper (block, ref) ──────┐
 *   │ <img ref/>  ← 자연 비율 그대로 렌더  │
 *   │ <div absolute inset-0 size=img>      │  ← overlay slot (M11 bbox)
 *   │   {children}                         │
 *   │ </div>                               │
 *   └──────────────────────────────────────┘
 *
 *   - overlay 의 left/top/width/height 는 img 의 offsetLeft/Top/Width/Height 에
 *     맞춰 ResizeObserver 로 실시간 동기화. wrapper 와 img 의 box 가
 *     어긋날 수 있는 모든 케이스(inline-block 폭 fallback, padding, max-width 등)
 *     를 회피한다.
 *
 * Props 정책:
 *   - ``imgStyle`` 로 호출측이 width/height 를 제어 (예: fit 모드 vs 100% 모드).
 *   - ``onImageLoad`` 로 naturalWidth 전달 → PdfPreviewPanel 이 zoom % 계산.
 */

import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";

import { usePageImage } from "./usePageImage";

interface Props {
  jobId: string;
  fileId: string;
  page: number;
  className?: string;
  /** img 자체의 width/height 스타일 — 줌 단계에서 사용. */
  imgStyle?: CSSProperties;
  /** 이미지 로드 완료 시 naturalWidth 콜백 (zoom % 계산용). */
  onImageLoad?: (naturalWidth: number, naturalHeight: number) => void;
  /** Overlay layer (예: bbox 하이라이트) — M11 에서 사용. */
  children?: ReactNode;
}

interface ImgRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export default function ImagePagePreview({
  jobId,
  fileId,
  page,
  className,
  imgStyle,
  onImageLoad,
  children,
}: Props) {
  const { t } = useTranslation();
  const { url, isLoading, error } = usePageImage(jobId, fileId, page);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgRect, setImgRect] = useState<ImgRect | null>(null);

  // img 의 실제 렌더 box 를 추적해 overlay 위치를 그것과 정확히 동기화.
  // 줌/리사이즈/이미지 교체 시점 모두 ResizeObserver 가 잡아준다.
  useEffect(() => {
    const node = imgRef.current;
    if (!node) return;
    const update = () => {
      setImgRect({
        left: node.offsetLeft,
        top: node.offsetTop,
        width: node.offsetWidth,
        height: node.offsetHeight,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [url, imgStyle]);

  if (error) {
    const msg = error instanceof ApiError ? error.detail : error.message;
    return (
      <div className="flex h-full min-h-[200px] items-center justify-center rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertCircle className="mr-2 h-4 w-4" />
        {t("preview.image.loadError", { page, message: msg })}
      </div>
    );
  }

  return (
    <div className={"relative inline-block " + (className ?? "")}>
      {url ? (
        <img
          ref={imgRef}
          src={url}
          alt={`PDF page ${page}`}
          className="block select-none"
          style={imgStyle}
          draggable={false}
          onLoad={(e) => {
            const target = e.currentTarget;
            onImageLoad?.(target.naturalWidth, target.naturalHeight);
            setImgRect({
              left: target.offsetLeft,
              top: target.offsetTop,
              width: target.offsetWidth,
              height: target.offsetHeight,
            });
          }}
        />
      ) : (
        <div className="flex min-h-[400px] min-w-[300px] items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      {/* loading 중에도 이전 이미지를 dim 처리해 깜빡임 최소화 */}
      {isLoading && url && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/30">
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
        </div>
      )}

      {/* Overlay slot — M11 의 BboxOverlay 등이 들어옴.
          img 의 실제 offset/size 를 직접 따라가 wrapper(inline-block)와의 미세
          어긋남(Tailwind preflight 의 max-width, inline-block 폭 fallback 등)을
          회피한다. children 이 없어도 빈 div 만 그리므로 부담 미미.
          부모 div 자체는 pointer-events-none 으로 두어 비활성 영역에선 이미지의
          pan/drag 가 그대로 동작하고, 클릭/hover 를 받아야 하는 자식만
          ``pointer-events-auto`` 로 명시. */}
      {imgRect && (
        <div
          className="pointer-events-none absolute"
          style={{
            left: imgRect.left,
            top: imgRect.top,
            width: imgRect.width,
            height: imgRect.height,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
